import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollText, MessageSquare, Mail, Search } from "lucide-react";
import { toast } from "sonner";

const SOURCE_LABELS = {
  manual: "Manual text",
  reminder: "Day-before reminder",
  booking_confirmation: "Booking confirmation",
  booking_received: "Request received",
  booking_declined: "Booking declined",
  booking_expired: "Request expired",
  reschedule: "Reschedule notice",
  manual_confirmation: "Confirmation email",
  owner_new_booking: "New booking alert (you)",
  owner_sms_alert: "Reply alert (you)",
  password_reset: "Password reset",
  team_invite: "Team invite",
  other: "Other",
};

const fmtWhen = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", {
    timeZone: "America/New_York",
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });
};

export default function MessageLog() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");

  const load = useCallback(async (query, msgType) => {
    try {
      const { data } = await api.get("/message-log", {
        params: { ...(query ? { q: query } : {}), ...(msgType && msgType !== "all" ? { msg_type: msgType } : {}), limit: 150 },
      });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q, type), q ? 350 : 0);
    return () => clearTimeout(t);
  }, [q, type, load]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
          <ScrollText className="w-6 h-6 text-blue-700" />
          Message log
        </h1>
        <p className="text-sm text-slate-500 mt-1">Every text and email the system has sent — manual or automatic.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by customer, phone, email, or content…" className="pl-9" data-testid="msglog-search" />
        </div>
        <div className="flex gap-1">
          {[["all", "All"], ["sms", "Texts"], ["email", "Emails"]].map(([v, l]) => (
            <Button key={v} size="sm" variant={type === v ? "default" : "outline"}
              className={type === v ? "bg-blue-700 hover:bg-blue-800" : ""}
              onClick={() => setType(v)} data-testid={`msglog-filter-${v}`}>
              {l}
            </Button>
          ))}
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {items === null ? (
            <div className="p-8 text-center text-slate-400 text-sm">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm" data-testid="msglog-empty">No messages found.</div>
          ) : (
            <div className="divide-y divide-slate-100" data-testid="msglog-list">
              {items.map((m) => (
                <div key={`${m.kind}-${m.id}`} className="px-4 py-3 flex items-start gap-3 hover:bg-slate-50" data-testid={`msglog-row-${m.id}`}>
                  <div className={`mt-0.5 w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${m.kind === "sms" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"}`}>
                    {m.kind === "sms" ? <MessageSquare className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      {m.customer_id ? (
                        <Link to={`/messages?customer=${m.customer_id}`} className="text-sm font-semibold text-slate-900 hover:text-blue-700">
                          {m.customer_name || "Unknown"}
                        </Link>
                      ) : (
                        <span className="text-sm font-semibold text-slate-900">{m.to}</span>
                      )}
                      <Badge variant="outline" className="rounded-full text-[10px] px-2 py-0 bg-slate-100 text-slate-700 border-slate-300">
                        {SOURCE_LABELS[m.source] || m.source}
                      </Badge>
                      {m.status && (
                        <span className={`text-[11px] ${m.status === "failed" ? "text-red-600 font-semibold" : "text-slate-400"}`}>
                          {m.status}
                        </span>
                      )}
                      <span className="text-[11px] text-slate-400 ml-auto shrink-0">{fmtWhen(m.created_at)}</span>
                    </div>
                    <div className="text-xs text-slate-600 truncate mt-0.5">
                      {m.kind === "email" ? <><span className="text-slate-400">to {m.to} — </span>{m.subject}</> : m.body}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
